/**
 * `/sessions/:id`: "Guidage" — the current door, in very large type, plus the
 * session banner and the four actions that move a volunteer through a
 * session (arrive, skip to the next door, stop, or close for everyone).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getBuilding,
  getCurrentAssignment,
  getSession,
  getSessionState,
  leaveSessionRequest,
  nextDoor,
  skipDoor,
  updatePosition,
} from '../../../api/sessions';
import { Button, Skeleton, useToast } from '../../../components/ui';
import { useAuth } from '../../../auth/AuthContext';
import { t } from '../../../i18n/fr';
import { useOfflineStatus } from '../../../offline/useOfflineStatus';
import { DoorHero } from '../components/DoorHero';
import { SessionBanner } from '../components/SessionBanner';
import { isNoCurrentDoorError, isNoDoorLeftError, sessionErrorMessage } from '../errors';
import { useOptionalPosition } from '../hooks/useOptionalPosition';

import type { Assignment, Building, SessionWithExtras } from '../../../api/types';

const STATE_POLL_MS = 15_000;
const POSITION_POST_MS = 60_000;

/**
 * Render the guidance screen.
 *
 * @returns The page element.
 */
export function GuidancePage() {
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { position, granted } = useOptionalPosition();
  const { online } = useOfflineStatus();

  const [session, setSession] = useState<SessionWithExtras | null>(null);
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [building, setBuilding] = useState<Building | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [noDoorLeft, setNoDoorLeft] = useState(false);
  const [busy, setBusy] = useState(false);

  const positionRef = useRef(position);
  positionRef.current = position;

  /** Fetch (or, if none is pending, request) the caller's current door. */
  const ensureCurrentDoor = useCallback(async (): Promise<void> => {
    const current = await getCurrentAssignment(sessionId);
    if (current) {
      setAssignment(current);
      setNoDoorLeft(false);
      return;
    }
    try {
      const assigned = await nextDoor(sessionId);
      setAssignment(assigned);
      setNoDoorLeft(false);
    } catch (error) {
      if (isNoDoorLeftError(error)) {
        setAssignment(null);
        setNoDoorLeft(true);
        return;
      }
      throw error;
    }
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const sessionData = await getSession(sessionId);
        if (cancelled) return;
        setSession(sessionData);
        await ensureCurrentDoor();
      } catch (error) {
        if (!cancelled) {
          showToast(sessionErrorMessage(error), 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, ensureCurrentDoor, showToast]);

  // The building view backs the door hero's badges; refetched whenever the
  // current door changes.
  useEffect(() => {
    if (!assignment) {
      setBuilding(undefined);
      return undefined;
    }
    let cancelled = false;
    getBuilding(sessionId)
      .then((data) => {
        if (!cancelled) {
          setBuilding(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBuilding(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [assignment, sessionId]);

  // Poll the light session state every 15s to keep the banner and the
  // participant list fresh while several binômes progress in parallel.
  useEffect(() => {
    const interval = setInterval(() => {
      getSessionState(sessionId)
        .then((state) => {
          setSession((current) =>
            current
              ? { ...current, participants: state.participants, progress: state.progress }
              : current,
          );
        })
        .catch(() => {
          // A missed poll is not worth surfacing; the next one retries.
        });
    }, STATE_POLL_MS);
    return () => clearInterval(interval);
  }, [sessionId]);

  // Report the live position on arrival, then every 60s. The first send is
  // not a nicety: the queue ranks doors by distance to the last known
  // position, and rejoining a session clears it. Without it, someone who
  // rejoins mid-street is served from the session's start point for a whole
  // minute — the wrong end of the walk.
  useEffect(() => {
    if (!granted) {
      return undefined;
    }
    const send = () => {
      if (positionRef.current) {
        updatePosition(sessionId, positionRef.current).catch(() => undefined);
      }
    };
    send();
    const interval = setInterval(send, POSITION_POST_MS);
    return () => clearInterval(interval);
  }, [granted, sessionId]);

  async function handleNext() {
    setBusy(true);
    try {
      const assigned = await nextDoor(sessionId);
      setAssignment(assigned);
      setNoDoorLeft(false);
    } catch (error) {
      if (isNoDoorLeftError(error)) {
        setAssignment(null);
        setNoDoorLeft(true);
      } else {
        showToast(sessionErrorMessage(error), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSkip() {
    setBusy(true);
    try {
      // `no_current_door` means this screen is showing a door that has since
      // been recorded elsewhere. The volunteer asked to move on; give them
      // the next door instead of an error they can do nothing with.
      const assigned = await skipDoor(sessionId).catch((error: unknown) =>
        isNoCurrentDoorError(error) ? nextDoor(sessionId) : Promise.reject(error),
      );
      setAssignment(assigned);
      setNoDoorLeft(false);
    } catch (error) {
      if (isNoDoorLeftError(error)) {
        setAssignment(null);
        setNoDoorLeft(true);
      } else {
        showToast(sessionErrorMessage(error), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleStop() {
    setBusy(true);
    try {
      await leaveSessionRequest(sessionId);
      navigate('/');
    } catch (error) {
      showToast(sessionErrorMessage(error), 'error');
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="pile" aria-hidden="true">
        <Skeleton height="3rem" />
        <Skeleton height="10rem" />
      </div>
    );
  }

  if (!session) {
    return null;
  }

  const canClose = session.me?.can_close ?? false;
  const referencePoint = position ?? session.start_point;
  const others = session.participants.filter((participant) => participant.user.id !== user?.id);

  return (
    <div className="pile">
      <SessionBanner
        name={session.name}
        participantsCount={session.participants.length}
        progress={session.progress}
        onClick={() => navigate('/sessions/join')}
      />

      {noDoorLeft ? (
        <div className="bloc">
          <h2>{t('session.guide.noDoorLeftTitle')}</h2>
          <p>{t('session.guide.noDoorLeftBody')}</p>
          <div className="pile" style={{ marginTop: 'var(--space-4)' }}>
            {canClose ? (
              <Button
                variant="primary"
                fullWidth
                onClick={() => navigate(`/sessions/${sessionId}/close`)}
              >
                {t('session.guide.closeButton')}
              </Button>
            ) : null}
            <Button variant="secondary" fullWidth disabled={busy} onClick={() => void handleStop()}>
              {t('session.guide.stopButton')}
            </Button>
          </div>
        </div>
      ) : assignment ? (
        <>
          <DoorHero assignment={assignment} referencePoint={referencePoint} building={building} />
          {others.length > 0 ? (
            <div className="bloc">
              <div className="binome">
                ● <b>{t('session.guide.binomeInfo')}</b> ·{' '}
                {others
                  .map(
                    (participant) =>
                      `${participant.user.first_name} · ${participant.current_door ?? '—'}`,
                  )
                  .join(' · ')}
              </div>
            </div>
          ) : null}
          <div className="bloc pile">
            <Button
              variant="primary"
              fullWidth
              onClick={() => navigate(`/sessions/${sessionId}/door`)}
            >
              {t('session.guide.arrivedButton')}
            </Button>
            <Button
              variant="secondary"
              fullWidth
              disabled={busy || !online}
              onClick={() => void handleSkip()}
            >
              {online ? t('session.guide.skipButton') : t('session.guide.skipButtonOffline')}
            </Button>
            <Button variant="ghost" fullWidth disabled={busy} onClick={() => void handleStop()}>
              {t('session.guide.stopButton')}
            </Button>
            {canClose ? (
              <Button
                variant="ghost"
                fullWidth
                onClick={() => navigate(`/sessions/${sessionId}/close`)}
              >
                {t('session.guide.closeButton')}
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <div className="bloc">
          <h2>{t('session.guide.noAssignmentTitle')}</h2>
          <p className="muted">{t('session.guide.noAssignmentBody')}</p>
          <Button variant="primary" fullWidth disabled={busy} onClick={() => void handleNext()}>
            {t('session.guide.getNextDoor')}
          </Button>
        </div>
      )}
    </div>
  );
}
